/**
 * Polymarket-style rolling PnL chart (ported from the legacy CC
 * recharts version onto ECharts, which the V3 base already ships —
 * recharts was dropped in the dependency merge).
 *
 *   - One series: the running total PnL (`cumulative_pnl_usdc`).
 *   - Smooth curve, no axes/grid/legend chart-junk; hover tooltip only.
 *   - Buckets raw ~10s stats to 5-minute intervals so the chart "ticks"
 *     once per market window.
 */
import { useMemo } from "react";
import { Text, useComputedColorScheme, useMantineTheme } from "@mantine/core";
import ReactECharts from "echarts-for-react";

export interface PnlPoint {
  /** ISO timestamp from /api/v1/agents/:id/stats */
  ts: string;
  /** PnL in USDC, parsed to number */
  pnl: number;
}

interface PnlChartProps {
  data: PnlPoint[];
  /** Bucket size used to downsample raw stats. Default 5 min. */
  bucketMs?: number;
  /** Pixel height of the chart area. */
  height?: number;
}

export function PnlChart({ data, bucketMs = 5 * 60 * 1000, height = 280 }: PnlChartProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("dark");
  const dark = colorScheme === "dark";

  const bucketed = useMemo(() => bucket(data, bucketMs), [data, bucketMs]);

  if (bucketed.length === 0) {
    return <Text c="dimmed">No PnL history yet.</Text>;
  }

  const lastPoint = bucketed[bucketed.length - 1];
  const last = lastPoint ? lastPoint.pnl : 0;
  const strokeColor = last >= 0 ? "#3b82f6" : "#ef4444"; // blue-500 / red-500

  // Pad the y-domain so a flat line doesn't collapse to zero height.
  const values = bucketed.map((b) => b.pnl);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const pad = Math.max(1, (maxV - minV) * 0.2);

  const option = {
    animation: false,
    grid: { left: 8, right: 8, top: 8, bottom: 8 },
    xAxis: { type: "time", show: false, min: "dataMin", max: "dataMax" },
    yAxis: { type: "value", show: false, min: minV - pad, max: maxV + pad },
    tooltip: {
      trigger: "axis",
      backgroundColor: dark ? theme.colors.dark[6] : theme.white,
      borderColor: dark ? theme.colors.dark[4] : theme.colors.gray[3],
      textStyle: { color: dark ? theme.white : theme.black, fontSize: 12 },
      axisPointer: {
        type: "line",
        lineStyle: {
          color: dark ? theme.colors.dark[3] : theme.colors.gray[4],
          type: "dashed",
        },
      },
      valueFormatter: (v: unknown) => {
        const n = typeof v === "number" ? v : Number(v);
        return `${n >= 0 ? "+" : ""}${n.toFixed(2)} USDC`;
      },
    },
    series: [
      {
        name: "PnL",
        type: "line",
        smooth: 0.4,
        showSymbol: false,
        data: bucketed.map((b) => [b.ts, b.pnl]),
        lineStyle: { width: 2.5, color: strokeColor, cap: "round", join: "round" },
        itemStyle: { color: strokeColor },
        emphasis: { scale: 1.4 },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height, width: "100%" }} notMerge />;
}

/**
 * Reduce raw stats to one point per `bucketMs` interval using the
 * latest (most recent) PnL value seen in that bucket. Buckets are
 * aligned to UTC epoch so they line up with the 5-minute market grid.
 */
function bucket(rows: PnlPoint[], bucketMs: number): { ts: number; pnl: number }[] {
  if (rows.length === 0) return [];
  const map = new Map<number, { ts: number; pnl: number; rawTs: number }>();
  for (const r of rows) {
    const t = new Date(r.ts).getTime();
    if (!Number.isFinite(t) || !Number.isFinite(r.pnl)) continue;
    const key = Math.floor(t / bucketMs) * bucketMs;
    const existing = map.get(key);
    if (existing === undefined || t > existing.rawTs) {
      map.set(key, { ts: key, pnl: r.pnl, rawTs: t });
    }
  }
  return Array.from(map.values())
    .sort((a, b) => a.ts - b.ts)
    .map(({ ts, pnl }) => ({ ts, pnl }));
}
